import { currentUser, filter, lastTicketIds, pollInterval, priFilter, selId, set, tickets } from '../state';
import type { Step, StepMsg } from '../types';
import { callApi, callGet } from './api';
import { isAdmin } from './auth';
import { buildRichEditor, esc, getRichData, parseEmp, pillClass, pillIcon, pillLabel, toast, topicIcon } from './ui';

const PRI_LABEL: Record<string, string> = { high: 'ด่วนมาก', medium: 'ปกติ', low: 'ไม่เร่งด่วน' };
const PRI_ICON:  Record<string, string> = { high: 'ti-alert-triangle', medium: 'ti-minus', low: 'ti-arrow-down' };

const STATUS_LABEL: Record<string, string> = { all: 'สถานะ: ทั้งหมด', new: 'ใหม่', processing: 'กำลังดำเนินการ', done: 'เสร็จแล้ว' };
const PRI_LABEL2:   Record<string, string> = { all: 'ระดับ: ทั้งหมด', high: 'ด่วนมาก', medium: 'ปกติ', low: 'ไม่เร่งด่วน' };

// ── Normalize step ──
export function normalizeStep(s: string | Step): { name: string; msg: StepMsg } {
  if (typeof s === 'string') return { name: s, msg: { text: '', imageUrl: '', links: [] } };
  const raw = s.msg;
  if (!raw) return { name: s.name ?? '', msg: { text: '', imageUrl: '', links: [] } };
  if (typeof raw === 'string') return { name: s.name ?? '', msg: { text: raw, imageUrl: '', links: [] } };
  return { name: s.name ?? '', msg: { text: (raw as StepMsg).text ?? '', imageUrl: (raw as StepMsg).imageUrl ?? '', links: (raw as StepMsg).links ?? [] } };
}

// ── Load ──
export async function loadTickets(manual = false): Promise<void> {
  const icon = document.getElementById('syncIcon')!;
  icon.classList.add('spin');
  const data = await callGet('tickets');
  if (data.ok) {
    const incomingTickets = (data.tickets as typeof tickets) ?? [];
    if (lastTicketIds.size > 0) {
      incomingTickets.filter(t => !lastTicketIds.has(t.id) && t.status === 'new').forEach(notifyNewTicket);
    }
    set.lastTicketIds(new Set(incomingTickets.map(t => t.id)));
    set.tickets(incomingTickets);
    updateStats();
    renderList();
    if (selId && manual) renderDetail(selId);
    const now = new Date().toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
    document.getElementById('hdrName')!.textContent = `${currentUser?.displayName} · ${now}`;
  } else {
    const list = document.getElementById('ticketList')!;
    list.innerHTML = '';
    const empty = document.createElement('div');
    empty.className = 'empty';
    empty.innerHTML = '<i class="ti ti-wifi-off" style="display:block;font-size:32px;margin-bottom:8px;color:var(--red)"></i>';
    const msg = document.createElement('div');
    msg.textContent = `เชื่อมต่อ API ไม่สำเร็จ: ${data.error ?? 'ไม่สามารถโหลด Ticket ได้'}`;
    const retryBtn = document.createElement('button');
    retryBtn.className = 'btn btn-ghost';
    retryBtn.style.cssText = 'margin-top:10px;font-size:12px';
    retryBtn.innerHTML = '<i class="ti ti-refresh"></i> ลองใหม่';
    retryBtn.onclick = () => loadTickets(true);
    empty.appendChild(msg);
    empty.appendChild(retryBtn);
    list.appendChild(empty);
  }
  icon.classList.remove('spin');
}

function notifyNewTicket(_t: unknown): void {
  try {
    const ctx = new AudioContext(), osc = ctx.createOscillator(), gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start(); osc.stop(ctx.currentTime + 0.4);
  } catch { /* AudioContext not available */ }
}

export function updateStats(): void {
  document.getElementById('st-all')!.textContent  = String(tickets.length);
  document.getElementById('st-new')!.textContent  = String(tickets.filter(t => t.status === 'new').length);
  document.getElementById('st-proc')!.textContent = String(tickets.filter(t => t.status === 'processing').length);
  document.getElementById('st-done')!.textContent = String(tickets.filter(t => t.status === 'done').length);
}

// ── Filter controls ──
export function setFilter(f: string): void {
  set.filter(f);
  const lbl = document.getElementById('cddStatusLabel');
  if (lbl) lbl.textContent = STATUS_LABEL[f] ?? f;
  document.querySelectorAll('#cddStatusMenu .cdd-item').forEach(el =>
    el.classList.toggle('active', (el as HTMLElement).dataset['val'] === f));
  renderList();
}

export function setPriFilter(p: string): void {
  set.priFilter(p);
  const lbl = document.getElementById('cddPriLabel');
  if (lbl) lbl.textContent = PRI_LABEL2[p] ?? p;
  document.querySelectorAll('#cddPriMenu .cdd-item').forEach(el =>
    el.classList.toggle('active', (el as HTMLElement).dataset['val'] === p));
  renderList();
}

// ── Render list ──
export function renderList(): void {
  const q = (document.getElementById('searchInp') as HTMLInputElement).value.toLowerCase();
  const list = document.getElementById('ticketList')!;
  let data = tickets;
  if (filter !== 'all')    data = data.filter(t => t.status === filter);
  if (priFilter !== 'all') data = data.filter(t => (t.priority ?? 'medium') === priFilter);
  if (q) data = data.filter(t =>
    t.id.toLowerCase().includes(q) ||
    t.empName.toLowerCase().includes(q) ||
    t.topic.toLowerCase().includes(q) ||
    t.detail.toLowerCase().includes(q)
  );
  if (!data.length) {
    list.innerHTML = '<div class="empty"><i class="ti ti-inbox-off"></i>ไม่พบ ticket</div>';
    return;
  }
  list.innerHTML = data.map(t => {
    const pc = pillClass(t.status), pl = pillLabel(t.status), pi = pillIcon(t.status);
    const pri = t.priority ?? 'medium';
    const time = t.updatedAt ? String(t.updatedAt).slice(0, 16) : '';
    const emp = parseEmp(t.empName);
    return `<div class="tk-card${selId === t.id ? ' sel' : ''}" onclick="selectTicket('${esc(t.id)}')">
      <div class="tk-top">
        <span class="tk-id">${esc(t.id)}</span>
        <div class="tk-badges">
          <span class="pill pri-${pri}" style="font-size:9px"><i class="ti ${PRI_ICON[pri]}"></i>${PRI_LABEL[pri] ?? pri}</span>
          <span class="pill ${pc}" style="font-size:9px"><i class="ti ${pi}"></i>${pl}</span>
        </div>
      </div>
      <div class="tk-meta">${esc(emp.empName)} — ${esc(emp.branch)}</div>
      <div class="tk-time">${esc(time)}${t.hrName ? ' · ' + esc(t.hrName) : ''}</div>
    </div>`;
  }).join('');
}

// ── Render detail ──
export function renderDetail(id: string): void {
  const t = tickets.find(x => x.id === id);
  if (!t) return;
  if (typeof t.steps === 'string') { try { t.steps = JSON.parse(t.steps as unknown as string); } catch { t.steps = []; } }
  if (!Array.isArray(t.steps) || !t.steps.length) t.steps = ['รับเรื่อง', 'ดำเนินการ', 'แจ้งผล'];
  const pct = t.status === 'done' ? 100 : Math.round((t.step / t.steps.length) * 100);
  const _steps = t.steps.map(s => normalizeStep(s));
  const stepName = t.status === 'done' ? 'เสร็จสิ้น' : _steps[Math.min(t.step, _steps.length - 1)].name;
  const canNext  = t.status !== 'done';
  const stepsHtml = _steps.map((s, i) => {
    let cls = 'step-item';
    if (i < t.step) cls += ' done';
    else if (i === t.step && t.status !== 'done') cls += ' active';
    const ic = i < t.step ? '<i class="ti ti-check" style="font-size:10px"></i>' : String(i + 1);
    return `<div class="${cls}"><div class="step-c">${ic}</div><div class="step-lbl">${esc(s.name)}</div></div>`;
  }).join('');
  const pc = pillClass(t.status), pl = pillLabel(t.status), pi = pillIcon(t.status);
  const _emp = parseEmp(t.empName);
  const hrDisplayName = t.hrName || currentUser?.displayName || 'ฝ่าย HR';
  const nextStep = canNext ? _steps[Math.min(t.step + 1, _steps.length - 1)] : null;
  const nextStepName = nextStep?.name ?? '';
  const nextMsg = (nextStep?.msg ?? {}) as { text?: string; imageUrl?: string; links?: Array<{ label: string; url: string }> };
  const defaultText = nextMsg.text ?? `อัปเดตเรื่องของคุณ\n\nTicket: ${t.id}\nหัวข้อ: ${t.topic}\n\nHR ผู้ดูแล: ${hrDisplayName}`;
  void nextStepName; // used in template below if needed

  document.getElementById('detailPane')!.innerHTML = `
    <div class="detail-card slide-in">
      <div class="detail-header">
        <div class="topic-icon"><i class="ti ${topicIcon(t.topic)}"></i></div>
        <div style="flex:1">
          <h2>${esc(t.topic)}</h2>
          <span style="font-size:11px;color:var(--muted)">${esc(t.id)}</span>
        </div>
        <span class="pill ${pc}"><i class="ti ${pi}"></i>${pl}</span>
      </div>
      <div class="detail-body">
        <div class="assign-box">
          <div class="assign-avatar ${t.hrName ? '' : 'unassigned'}">
            <i class="ti ${t.hrName ? 'ti-user-check' : 'ti-user-question'}"></i>
          </div>
          <div style="flex:1;min-width:0">
            <div class="assign-label">HR ผู้รับผิดชอบ</div>
            <div class="assign-name">${esc(t.hrName || 'ยังไม่ได้รับมอบหมาย')}</div>
          </div>
          ${t.hrName
            ? `<button class="btn btn-ghost" style="font-size:12px" onclick="openNotify('${esc(t.id)}')"><i class="ti ti-message"></i> ส่งข้อความ</button>`
            : `<button class="btn btn-primary" style="font-size:12px;padding:6px 12px" onclick="assignSelf('${esc(t.id)}')"><i class="ti ti-check"></i> รับเรื่อง (${esc(currentUser?.displayName ?? '')})</button>`
          }
        </div>
        <div class="prog-wrap">
          <div class="prog-row"><span>ความคืบหน้า</span><span style="font-weight:700;color:var(--primary)">${pct}%</span></div>
          <div class="prog-bar"><div class="prog-fill" style="width:${pct}%"></div></div>
          <div style="font-size:11px;color:var(--muted);margin-top:4px">ขั้นตอนปัจจุบัน: <strong>${esc(stepName)}</strong></div>
        </div>
        <div class="step-track">${stepsHtml}</div>
        <div class="section-label"><i class="ti ti-alert-triangle"></i>ระดับความสำคัญ</div>
        <div class="priority-select">
          ${(['high', 'medium', 'low'] as const).map(p =>
            `<button onclick="changePriority('${esc(t.id)}','${p}')" class="${(t.priority ?? 'medium') === p ? 'active-' + p : ''}">${{ high: '🔴 ด่วนมาก', medium: '🟡 ปกติ', low: '⚪ ไม่เร่งด่วน' }[p]}</button>`
          ).join('')}
        </div>
        <div class="info-grid" style="grid-template-columns:1fr 1fr 1fr;gap:8px;margin-bottom:14px">
          <div class="info-item"><label><i class="ti ti-id-badge"></i>รหัสพนักงาน</label><span>${esc(_emp.empId)}</span></div>
          <div class="info-item"><label><i class="ti ti-user"></i>ชื่อ-นามสกุล</label><span>${esc(_emp.empName)}</span></div>
          <div class="info-item"><label><i class="ti ti-building-store"></i>สาขา</label><span>${esc(_emp.branch)}</span></div>
          <div class="info-item"><label><i class="ti ti-calendar-plus"></i>สร้างเมื่อ</label><span>${esc(t.createdAt || '—')}</span></div>
          <div class="info-item"><label><i class="ti ti-clock"></i>อัปเดตล่าสุด</label><span>${esc(t.updatedAt || '—')}</span></div>
          <div class="info-item"><label><i class="ti ti-user-check"></i>HR ผู้ดูแล</label><span>${esc(t.hrName || '—')}</span></div>
        </div>
        <div style="background:var(--bg);border-radius:var(--r-sm);padding:12px;margin-bottom:14px;border:1px solid var(--border)">
          <div class="section-label"><i class="ti ti-align-left"></i>รายละเอียด</div>
          <div style="font-size:13px;line-height:1.7">${esc(t.detail)}</div>
        </div>
        ${canNext ? `<div class="step-msg-box">
          <label><i class="ti ti-brand-line"></i> ข้อความ LINE ที่จะส่งพร้อมอัปเดต (แก้ไขได้)</label>
          ${buildRichEditor(defaultText, nextMsg.imageUrl ?? '', nextMsg.links ?? [], 'ข้อความถึงพนักงาน...')}
        </div>` : ''}
        <div class="section-label"><i class="ti ti-note"></i>หมายเหตุ HR (ภายใน)</div>
        <div class="notes-box">${esc(t.notes || '—')}</div>
        <div class="note-input">
          <input type="text" id="noteInp" placeholder="เพิ่มหมายเหตุ..." onkeydown="if(event.key==='Enter')addNote('${esc(t.id)}')">
          <button class="btn btn-ghost" onclick="addNote('${esc(t.id)}')"><i class="ti ti-plus"></i></button>
        </div>
        <div class="actions">
          <button class="btn btn-primary" onclick="advance('${esc(t.id)}')" ${canNext ? '' : 'disabled'}>
            <i class="ti ti-arrow-right"></i> ขั้นตอนถัดไป
          </button>
          ${t.status !== 'done' ? `<button class="btn btn-ghost" onclick="closeTicketUI('${esc(t.id)}')"><i class="ti ti-circle-check"></i> ปิดงาน</button>` : ''}
        </div>
      </div>
    </div>`;
}

// ── Actions ──
export async function advance(id: string): Promise<void> {
  const t = tickets.find(x => x.id === id);
  if (!t || t.status === 'done') return;
  const msgBox = document.querySelector('.step-msg-box') as HTMLElement | null;
  const msgData = msgBox ? getRichData(msgBox) : { text: '', imageUrl: '', links: [] };
  t.step++; if (t.step >= t.steps.length) t.status = 'done'; else t.status = 'processing';
  t.hrName = currentUser?.displayName ?? ''; t.updatedAt = new Date().toLocaleString('th-TH');
  updateStats(); renderList(); renderDetail(id);
  const data = await callApi({ action: 'advance', ticketId: id, hrName: currentUser?.displayName, stepMsg: msgData.text, stepMsgImage: msgData.imageUrl, stepMsgLinks: msgData.links });
  if (!data.ok) { toast('error', data.error ?? ''); return; }
  const lineResult = data['lineResult'] as { ok: boolean } | undefined;
  toast(lineResult && !lineResult.ok ? 'warning' : 'success', lineResult && !lineResult.ok ? 'อัปเดตแล้ว แต่ส่ง LINE ไม่สำเร็จ' : 'อัปเดตและส่ง LINE แล้ว');
}

export async function changePriority(id: string, priority: string): Promise<void> {
  const t = tickets.find(x => x.id === id);
  if (!t) return;
  t.priority = priority as 'high' | 'medium' | 'low';
  renderList(); renderDetail(id);
  const data = await callApi({ action: 'setPriority', ticketId: id, priority });
  if (!data.ok) toast('error', data.error ?? 'เปลี่ยน priority ไม่สำเร็จ');
}

export async function assignSelf(id: string): Promise<void> {
  const t = tickets.find(x => x.id === id);
  if (!t) return;
  t.hrName = currentUser?.displayName ?? '';
  renderDetail(id); renderList();
  const data = await callApi({ action: 'assign', ticketId: id, hrName: currentUser?.displayName });
  toast(data.ok ? 'success' : 'error', data.ok ? `รับเรื่องแล้ว (${currentUser?.displayName})` : String(data.error));
}

export async function closeTicketUI(id: string): Promise<void> {
  const t = tickets.find(x => x.id === id);
  if (!t) return;
  t.status = 'done'; t.step = t.steps.length; t.hrName = currentUser?.displayName ?? '';
  t.updatedAt = new Date().toLocaleString('th-TH');
  updateStats(); renderList(); renderDetail(id);
  const data = await callApi({ action: 'done', ticketId: id, hrName: currentUser?.displayName });
  toast(data.ok ? 'success' : 'error', data.ok ? 'ปิดงานแล้ว' : String(data.error));
}

export async function addNote(id: string): Promise<void> {
  const note = (document.getElementById('noteInp') as HTMLInputElement | null)?.value.trim();
  if (!note) return;
  const t = tickets.find(x => x.id === id);
  if (!t) return;
  const ts = new Date().toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
  t.notes = (t.notes && t.notes !== '—' ? t.notes + '\n' : '') + `[${ts}] ${currentUser?.displayName}: ${note}`;
  (document.getElementById('noteInp') as HTMLInputElement).value = '';
  renderDetail(id);
  await callApi({ action: 'addNote', ticketId: id, note: `[${currentUser?.displayName}] ${note}` });
  toast('success', 'บันทึกหมายเหตุแล้ว');
}

// ── Notify ──
export function openNotify(id: string): void {
  set.notifyId(id);
  const t = tickets.find(x => x.id === id);
  (document.getElementById('notifyMsg') as HTMLTextAreaElement).value = t
    ? `เรียน คุณ${t.empName}\n\nฝ่าย HR มีข้อความเกี่ยวกับเรื่อง "${t.topic}" (${t.id})\n\n\n— ${currentUser?.displayName} (ฝ่าย HR)`
    : '';
  document.getElementById('notifyModal')!.classList.add('open');
}

export async function sendNotify(): Promise<void> {
  const msg = (document.getElementById('notifyMsg') as HTMLTextAreaElement).value.trim();
  if (!msg) return;
  import('./ui').then(({ closeModal }) => closeModal('notifyModal'));
  const data = await callApi({ action: 'notify', ticketId: (await import('../state')).notifyId, message: msg });
  toast(data.ok ? 'success' : 'error', data.ok ? 'ส่ง LINE สำเร็จ' : String(data.error));
}

// ── Selection ──
export function selectTicket(id: string): void {
  set.selId(id); renderList(); renderDetail(id);
  if (window.innerWidth <= 768) document.getElementById('detailPane')!.classList.add('mob-open');
}

export function closeMobileDetail(): void {
  document.getElementById('detailPane')!.classList.remove('mob-open');
  set.selId(null); renderList();
}

// ── Page nav ──
export function switchPage(page: string): void {
  if (page === 'admin' && !isAdmin()) { toast('warning', 'ไม่มีสิทธิ์เข้าถึงหน้านี้'); return; }
  document.querySelectorAll('.nav-tab').forEach(b => b.classList.toggle('active', (b as HTMLElement).dataset['page'] === page));
  const isSum = page === 'summary', isAdm = page === 'admin';
  document.getElementById('mainContent')!.classList.toggle('hidden', isSum || isAdm);
  document.getElementById('summaryPage')!.classList.toggle('active', isSum);
  document.getElementById('adminPage')!.classList.toggle('active', isAdm);
  if (isSum) import('./summary').then(({ renderSummary }) => renderSummary());
  if (isAdm) import('./admin/users').then(({ initAdminPage }) => initAdminPage());
}
